// src/services/productsService.js
//
// Frontend API wrapper for Products in the Catalog domain.
//
// PURPOSE
// -------
// This service handles concrete product records maintained by employees.
//
// In the current architecture a product row represents one sellable and
// stock-manageable article variant / SKU.
//
// Examples:
// - Deodorant Blue 100 ml
// - Deodorant Blue 200 ml
// - Face Cream 50 g
//
// This prepares the app for:
// - website product variants,
// - barcode scanning,
// - future inventory / warehouse module,
// - VAT-aware product pricing,
// - product image publication to the website.
//
// IMPORTANT MODEL NOTE
// --------------------
// Legacy product fields are no longer part of the final product model:
//
// Removed:
// - name_internal
// - size_or_capacity
// - price
//
// Current source-of-truth fields are:
//
// Identity / website:
// - category_id
// - name_public
// - brand
// - sku
// - barcode
//
// Variant / packaging:
// - variant_group_key
// - variant_label
// - packaging_label
// - content_quantity
// - content_unit_code
// - stock_unit_code
//
// Pricing:
// - price_entry_mode
// - price_input
// - vat_rate_percent
// - price_net
// - price_vat
// - price_gross
//
// Product images:
// - uploaded via multipart/form-data
// - metadata is stored in product_images
// - actual files are stored by backend upload handling
//
// ERROR HANDLING
// --------------
// This service follows the existing hb_visits pattern:
// - local frontend validation for obvious missing data,
// - backend remains authoritative for business rules,
// - errors are normalized into regular Error objects,
// - optional debug logging is controlled by REACT_APP_DEBUG_API.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

/**
 * Supported price entry modes.
 *
 * These values must match backend controller validation.
 */
export const PRODUCT_PRICE_ENTRY_MODES = Object.freeze({
  GROSS_INCLUDES_VAT: "GROSS_INCLUDES_VAT",
  NET_PLUS_VAT: "NET_PLUS_VAT",
});

/**
 * Extract a meaningful error message from an Axios/backend error object.
 */
function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

/**
 * Convert any API/transport error into a regular Error with useful metadata.
 */
function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;
  e.code = err?.response?.data?.code;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[ProductsService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Check whether a value is empty from the form/API perspective.
 */
function isBlank(v) {
  return v === undefined || v === null || `${v}`.trim() === "";
}

/**
 * Validate an id parameter.
 */
function assertId(context, id) {
  if (isBlank(id)) {
    const e = new Error(`${context}: id is required.`);
    e.code = "PRODUCT_ID_MISSING";
    throw e;
  }
}

/**
 * Validate payload object existence.
 */
function assertPayload(context, payload) {
  if (!payload || typeof payload !== "object") {
    const e = new Error(`${context}: payload object is required.`);
    e.code = "PRODUCT_PAYLOAD_MISSING";
    throw e;
  }
}

/**
 * Normalize optional text fields.
 *
 * Why?
 * - Empty string should usually be sent as null for optional DB columns.
 * - Unique nullable fields such as sku/barcode must not be sent as "".
 *
 * Important:
 * MySQL allows multiple NULL values in UNIQUE indexes, but only one empty
 * string value would be allowed. Therefore sku/barcode must be normalized.
 */
function cleanOptionalText(v) {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s || null;
}

/**
 * Normalize required text fields.
 */
function cleanRequiredText(v) {
  return String(v || "").trim();
}

/**
 * Normalize optional decimal-like values.
 *
 * The backend will still perform final validation and calculation.
 * This helper only makes the payload cleaner and avoids sending "".
 */
function cleanOptionalNumber(v) {
  if (v === undefined || v === null || `${v}`.trim() === "") return null;
  return v;
}

/**
 * Fetch a protected product image as Blob.
 *
 * Why this exists:
 * - <img src="/catalog/products/:id/images/:imageId/file"> does NOT use apiClient.
 * - Therefore it does not automatically include auth/session headers managed by apiClient.
 * - Backoffice images are protected endpoints, so we fetch them through apiClient
 *   and then render them as browser object URLs.
 */
export const fetchProductImageBlob = async (imageOrUrl) => {
  try {
    const rawUrl =
      typeof imageOrUrl === "string"
        ? imageOrUrl
        : imageOrUrl?.file_url || imageOrUrl?.url || "";

    if (!rawUrl || `${rawUrl}`.trim() === "") {
      const e = new Error("fetchProductImageBlob: image file_url is required.");
      e.code = "PRODUCT_IMAGE_URL_MISSING";
      throw e;
    }

    const cleanUrl = String(rawUrl).startsWith("/")
      ? String(rawUrl)
      : `/${rawUrl}`;

    const res = await apiClient.get(cleanUrl, {
      withCredentials: true,
      responseType: "blob",
    });

    return res?.data;
  } catch (err) {
    throwApiError("fetchProductImageBlob", err);
  }
};

/**
 * Build a usable image URL for <img src="...">.
 *
 * Backend returns image.file_url as a backend-relative route, for example:
 *   /catalog/products/:productId/images/:imageId/file
 *
 * Important:
 * Axios automatically uses apiClient.baseURL for API requests, but <img src>
 * does not. Therefore images need a real URL.
 *
 * If REACT_APP_API_URL is configured, it is used as prefix.
 * In production, when frontend/backend are served behind the same proxy,
 * the relative path can also work without a prefix.
 */
export function buildProductImageUrl(imageOrPath) {
  const path =
    typeof imageOrPath === "string"
      ? imageOrPath
      : imageOrPath?.file_url ||
        imageOrPath?.url ||
        imageOrPath?.path ||
        imageOrPath?.public_url ||
        "";

  if (!path) return "";

  if (/^https?:\/\//i.test(path)) return path;

  const base = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const cleanPath = String(path).startsWith("/") ? String(path) : `/${path}`;

  return `${base}${cleanPath}`;
}

/**
 * Prepare product payload before sending it to the backend.
 *
 * This keeps frontend forms flexible:
 * - optional empty fields become null,
 * - strings are trimmed,
 * - legacy fields are not sent,
 * - frontend does not calculate final VAT values; backend remains the source
 *   of truth for price_net / price_vat / price_gross.
 */
function normalizeProductPayload(payload) {
  const priceEntryMode =
    cleanOptionalText(payload.price_entry_mode) ||
    PRODUCT_PRICE_ENTRY_MODES.GROSS_INCLUDES_VAT;

  return {
    category_id: payload.category_id,

    // Public identity
    name_public: cleanRequiredText(payload.name_public),
    brand: cleanOptionalText(payload.brand),

    // Product identifiers
    sku: cleanOptionalText(payload.sku),
    barcode: cleanOptionalText(payload.barcode),

    // Website variant foundation
    variant_group_key: cleanOptionalText(payload.variant_group_key),
    variant_label: cleanOptionalText(payload.variant_label),

    // Packaging / content
    packaging_label: cleanOptionalText(payload.packaging_label),
    content_quantity: cleanOptionalNumber(payload.content_quantity),
    content_unit_code: cleanOptionalText(payload.content_unit_code),

    // Inventory foundation
    stock_unit_code: cleanRequiredText(payload.stock_unit_code || "pcs"),
    tracks_batches: !!payload.tracks_batches,
    requires_batch_on_receipt: !!payload.requires_batch_on_receipt,
    requires_expiry_on_receipt: !!payload.requires_expiry_on_receipt,
    default_issue_batch_policy: cleanOptionalText(payload.default_issue_batch_policy) || "none",
    allow_issue_without_batch: payload.allow_issue_without_batch === undefined ? true : !!payload.allow_issue_without_batch,
    stock_valuation_method: cleanOptionalText(payload.stock_valuation_method) || "product_moving_average",

    // Description
    description_short: cleanOptionalText(payload.description_short),

    // Pricing input.
    //
    // Backend calculates:
    // - price_net
    // - price_vat
    // - price_gross
    price_entry_mode: priceEntryMode,
    price_input: cleanOptionalNumber(payload.price_input),
    vat_rate_percent:
      payload.vat_rate_percent === undefined ||
      payload.vat_rate_percent === null ||
      `${payload.vat_rate_percent}`.trim() === ""
        ? 0
        : payload.vat_rate_percent,

    // Operational flags
    sort_order:
      payload.sort_order === undefined ||
      payload.sort_order === null ||
      `${payload.sort_order}`.trim() === ""
        ? 0
        : payload.sort_order,

    is_active: payload.is_active === undefined ? true : !!payload.is_active,
    publish_to_website:
      payload.publish_to_website === undefined
        ? true
        : !!payload.publish_to_website,
  };
}

/**
 * Validate product payload before create/update.
 *
 * This validation only checks obvious frontend errors.
 * Backend remains authoritative for:
 * - uniqueness of sku/barcode,
 * - valid category,
 * - valid unit codes,
 * - VAT calculation,
 * - publication/delete business rules.
 */
function assertProductPayload(context, payload) {
  assertPayload(context, payload);

  if (isBlank(payload.category_id)) {
    const e = new Error(`${context}: category_id is required.`);
    e.code = "PRODUCT_CATEGORY_ID_MISSING";
    throw e;
  }

  if (isBlank(payload.name_public)) {
    const e = new Error(`${context}: name_public is required.`);
    e.code = "PRODUCT_NAME_PUBLIC_MISSING";
    throw e;
  }

  if (isBlank(payload.stock_unit_code)) {
    const e = new Error(`${context}: stock_unit_code is required.`);
    e.code = "PRODUCT_STOCK_UNIT_MISSING";
    throw e;
  }

  const tracksBatches = !!payload.tracks_batches;
  const requiresBatch = !!payload.requires_batch_on_receipt;
  const requiresExpiry = !!payload.requires_expiry_on_receipt;
  const allowIssueWithoutBatch = payload.allow_issue_without_batch === undefined ? true : !!payload.allow_issue_without_batch;
  const batchPolicy = cleanOptionalText(payload.default_issue_batch_policy) || "none";
  const stockValuationMethod = cleanOptionalText(payload.stock_valuation_method) || "product_moving_average";

  if ((requiresBatch || requiresExpiry) && !tracksBatches) {
    const e = new Error(`${context}: batch/expiry requirements require batch tracking.`);
    e.code = "PRODUCT_TRACKS_BATCHES_REQUIRED";
    throw e;
  }

  if (requiresExpiry && !requiresBatch) {
    const e = new Error(`${context}: expiry on receipt requires batch on receipt.`);
    e.code = "PRODUCT_BATCH_REQUIRED_FOR_EXPIRY";
    throw e;
  }

  if (tracksBatches && !allowIssueWithoutBatch && batchPolicy === "none") {
    const e = new Error(`${context}: default issue batch policy must be FEFO or MANUAL when batchless issues are not allowed.`);
    e.code = "PRODUCT_DEFAULT_BATCH_POLICY_REQUIRED";
    throw e;
  }

  if (!["product_moving_average", "batch_moving_average"].includes(stockValuationMethod)) {
    const e = new Error(`${context}: stock valuation method is invalid.`);
    e.code = "PRODUCT_STOCK_VALUATION_METHOD_INVALID";
    throw e;
  }

  if (stockValuationMethod === "batch_moving_average") {
    if (!tracksBatches || !requiresBatch) {
      const e = new Error(`${context}: batch valuation requires batch tracking and batch on receipt.`);
      e.code = "PRODUCT_BATCH_VALUATION_REQUIRES_RECEIPT_BATCH";
      throw e;
    }
    if (allowIssueWithoutBatch || batchPolicy === "none") {
      const e = new Error(`${context}: batch valuation requires FEFO or MANUAL allocation for outgoing movements.`);
      e.code = "PRODUCT_BATCH_VALUATION_REQUIRES_ISSUE_ALLOCATION";
      throw e;
    }
  }

  const mode =
    cleanOptionalText(payload.price_entry_mode) ||
    PRODUCT_PRICE_ENTRY_MODES.GROSS_INCLUDES_VAT;

  if (!Object.values(PRODUCT_PRICE_ENTRY_MODES).includes(mode)) {
    const e = new Error(`${context}: price_entry_mode is invalid.`);
    e.code = "PRODUCT_PRICE_ENTRY_MODE_INVALID";
    throw e;
  }

  // Variant grouping rule:
  //
  // A product can exist without website variant grouping.
  // However, if a variant_group_key is provided, a variant_label should also be
  // provided. Otherwise the website cannot display a meaningful variant option.
  if (!isBlank(payload.variant_group_key) && isBlank(payload.variant_label)) {
    const e = new Error(
      `${context}: variant_label is required when variant_group_key is provided.`
    );
    e.code = "PRODUCT_VARIANT_LABEL_MISSING";
    throw e;
  }

  // Content unit rule:
  //
  // If content_quantity is provided, content_unit_code should also be provided.
  // Example:
  //   content_quantity = 100
  //   content_unit_code = ml
  if (!isBlank(payload.content_quantity) && isBlank(payload.content_unit_code)) {
    const e = new Error(
      `${context}: content_unit_code is required when content_quantity is provided.`
    );
    e.code = "PRODUCT_CONTENT_UNIT_MISSING";
    throw e;
  }
}

/**
 * Fetch products.
 *
 * Backend:
 *   GET /catalog/products
 *
 * Optional query params:
 * - active=true|false
 * - publish_to_website=true|false
 * - category_id=<id>
 * - variant_group_key=<key>
 * - q=<search text>
 *
 * Return:
 * - expected: array of product objects
 * - defensive fallback: [] if backend payload is missing/malformed
 */
export const fetchProducts = async (params = {}) => {
  try {
    const res = await apiClient.get("/catalog/products", {
      withCredentials: true,
      params,
    });

    const payload = res?.data;
    return Array.isArray(payload) ? payload : [];
  } catch (err) {
    throwApiError("fetchProducts", err);
  }
};

/**
 * Fetch one product by id.
 *
 * Backend:
 *   GET /catalog/products/:id
 *
 * Used by:
 * - ProductPage edit mode
 * - future detail previews
 */
export const fetchProductById = async (id) => {
  try {
    assertId("fetchProductById", id);

    const res = await apiClient.get(`/catalog/products/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? null;
  } catch (err) {
    throwApiError("fetchProductById", err);
  }
};

/**
 * Find one product by barcode.
 *
 * Backend:
 *   GET /catalog/products/by-barcode/:barcode
 *
 * This is a foundation endpoint for scanner-based workflows such as:
 * - goods receipt
 * - stock correction
 * - inventory counting
 * - POS-like quick lookup
 *
 * Barcode is treated as text, not as a number.
 */
export const fetchProductByBarcode = async (barcode) => {
  try {
    if (isBlank(barcode)) {
      const e = new Error("fetchProductByBarcode: barcode is required.");
      e.code = "PRODUCT_BARCODE_MISSING";
      throw e;
    }

    const encoded = encodeURIComponent(String(barcode).trim());

    const res = await apiClient.get(`/catalog/products/by-barcode/${encoded}`, {
      withCredentials: true,
    });

    return res?.data ?? null;
  } catch (err) {
    throwApiError("fetchProductByBarcode", err);
  }
};

/**
 * Create a new product / SKU.
 *
 * Backend:
 *   POST /catalog/products
 *
 * The frontend sends only input/source fields.
 * Backend calculates:
 * - price_net
 * - price_vat
 * - price_gross
 */
export const createProduct = async (payload) => {
  try {
    assertProductPayload("createProduct", payload);

    const normalizedPayload = normalizeProductPayload(payload);

    const res = await apiClient.post("/catalog/products", normalizedPayload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return res?.data;
  } catch (err) {
    throwApiError("createProduct", err);
  }
};

/**
 * Update an existing product / SKU.
 *
 * Backend:
 *   PUT /catalog/products/:id
 */
export const updateProduct = async (id, payload) => {
  try {
    assertId("updateProduct", id);
    assertProductPayload("updateProduct", payload);

    const normalizedPayload = normalizeProductPayload(payload);

    const res = await apiClient.put(
      `/catalog/products/${id}`,
      normalizedPayload,
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("updateProduct", err);
  }
};

/**
 * Toggle active state of a product.
 *
 * Backend:
 *   PATCH /catalog/products/:id/active
 *
 * Body:
 *   { active: true|false }
 */
export const toggleProductActive = async (id, active) => {
  try {
    assertId("toggleProductActive", id);

    const res = await apiClient.patch(
      `/catalog/products/${id}/active`,
      { active: !!active },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("toggleProductActive", err);
  }
};

/**
 * Delete a concrete product.
 *
 * Backend:
 *   DELETE /catalog/products/:id
 *
 * Backend may reject delete when:
 * - the product already has publication history,
 * - business rules do not allow physical removal.
 *
 * When physical delete is allowed, backend should also remove:
 * - product_images rows,
 * - physical image files.
 */
export const deleteProduct = async (id) => {
  try {
    assertId("deleteProduct", id);

    const res = await apiClient.delete(`/catalog/products/${id}`, {
      withCredentials: true,
    });

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteProduct", err);
  }
};

/**
 * Upload one or more product images.
 *
 * Backend:
 *   POST /catalog/products/:id/images
 *
 * Request:
 *   multipart/form-data
 *   files field name: images
 *
 * Browser security note:
 * - A web app cannot read arbitrary local file paths from the user's machine.
 * - "Selecting a path" is implemented through the standard file picker.
 * - Drag and drop provides File objects as well.
 *
 * Accepted files are validated by backend.
 */
export const uploadProductImages = async (productId, files) => {
  try {
    assertId("uploadProductImages", productId);

    const fileList = Array.isArray(files) ? files : Array.from(files || []);

    if (fileList.length === 0) {
      const e = new Error("uploadProductImages: at least one file is required.");
      e.code = "PRODUCT_IMAGE_FILES_MISSING";
      throw e;
    }

    const formData = new FormData();

    fileList.forEach((file) => {
      formData.append("images", file);
    });

    const res = await apiClient.post(
      `/catalog/products/${productId}/images`,
      formData,
      {
        withCredentials: true,

        // Important:
        // Do not manually set multipart boundary.
        // Axios/browser will set Content-Type with boundary automatically.
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("uploadProductImages", err);
  }
};

/**
 * Mark one image as the primary product image.
 *
 * Backend:
 *   PATCH /catalog/products/:productId/images/:imageId/primary
 *
 * The primary image is used by:
 * - product overview,
 * - product card previews,
 * - future website publication payload.
 */
export const setProductImagePrimary = async (productId, imageId) => {
  try {
    assertId("setProductImagePrimary productId", productId);
    assertId("setProductImagePrimary imageId", imageId);

    const res = await apiClient.patch(
      `/catalog/products/${productId}/images/${imageId}/primary`,
      {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("setProductImagePrimary", err);
  }
};

/**
 * Backward-compatible alias.
 *
 * Some previous snippets used the name setPrimaryProductImage.
 * Keeping this alias prevents import mismatches while the frontend files are
 * being aligned.
 */
export const setPrimaryProductImage = setProductImagePrimary;

/**
 * Delete one product image.
 *
 * Backend:
 *   DELETE /catalog/products/:productId/images/:imageId
 *
 * Backend should remove:
 * - metadata row from product_images,
 * - physical file from upload storage.
 */
export const deleteProductImage = async (productId, imageId) => {
  try {
    assertId("deleteProductImage productId", productId);
    assertId("deleteProductImage imageId", imageId);

    const res = await apiClient.delete(
      `/catalog/products/${productId}/images/${imageId}`,
      {
        withCredentials: true,
      }
    );

    return res?.data ?? { ok: true };
  } catch (err) {
    throwApiError("deleteProductImage", err);
  }
};

export default {
  PRODUCT_PRICE_ENTRY_MODES,

  buildProductImageUrl,

  fetchProducts,
  fetchProductById,
  fetchProductByBarcode,
  fetchProductImageBlob,

  createProduct,
  updateProduct,
  toggleProductActive,
  deleteProduct,

  uploadProductImages,
  setProductImagePrimary,
  setPrimaryProductImage,
  deleteProductImage,
};



